/**
 * Relationship Entry API Service
 *
 * API client functions for relationship tracker daily sentiment entries.
 * Provides CRUD operations, date range queries, and statistics.
 */

import { apiClient } from "./api";
import type {
  RelationshipEntry,
  RelationshipEntryCreate,
  RelationshipEntryListResponse,
  RelationshipEntryStats,
  RelationshipEntryUpdate,
} from "@/types/relationshipEntry";

const API_PREFIX = import.meta.env.VITE_API_V1_PREFIX || "/api/v1";

/**
 * Create or update a relationship entry for a date (upsert)
 */
export async function createEntry(
  data: RelationshipEntryCreate
): Promise<RelationshipEntry> {
  const response = await apiClient.post(
    `${API_PREFIX}/relationship-entries`,
    data
  );
  return response.data;
}

/**
 * List entries within a date range
 */
export async function listEntries(
  startDate: string,
  endDate: string
): Promise<RelationshipEntryListResponse> {
  const response = await apiClient.get(`${API_PREFIX}/relationship-entries`, {
    params: { start_date: startDate, end_date: endDate },
  });
  return response.data;
}

/**
 * Get a single entry by ID
 */
export async function getEntry(entryId: string): Promise<RelationshipEntry> {
  const response = await apiClient.get(
    `${API_PREFIX}/relationship-entries/${entryId}`
  );
  return response.data;
}

/**
 * Get entry for a specific date
 */
export async function getEntryByDate(date: string): Promise<RelationshipEntry> {
  const response = await apiClient.get(
    `${API_PREFIX}/relationship-entries/date/${date}`
  );
  return response.data;
}

/**
 * Update an existing entry
 */
export async function updateEntry(
  entryId: string,
  data: RelationshipEntryUpdate
): Promise<RelationshipEntry> {
  const response = await apiClient.patch(
    `${API_PREFIX}/relationship-entries/${entryId}`,
    data
  );
  return response.data;
}

/**
 * Delete an entry
 */
export async function deleteEntry(entryId: string): Promise<void> {
  await apiClient.delete(`${API_PREFIX}/relationship-entries/${entryId}`);
}

/**
 * Get aggregate statistics for a date range
 */
export async function getStats(
  startDate: string,
  endDate: string
): Promise<RelationshipEntryStats> {
  const response = await apiClient.get(
    `${API_PREFIX}/relationship-entries/stats`,
    { params: { start_date: startDate, end_date: endDate } }
  );
  return response.data;
}
